import get from "lodash/get";
import map from "lodash/map";
import {
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_METADATA_KEY,
  FIELD_SUBTEMPLATE_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { Sections } from "PFApp/types/activity_form/activity_form_schema";
import { Select } from "PFComponents/select";
import { Subtemplate } from "PFCore/types";
import { Option } from "PFTypes/options";
import { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "../../activity_form_store";
import { useDynamicSubtemplateFields } from "../../hooks/use_dynamic_subtemplate_fields";
import { useSubtemplate } from "../../hooks/use_subtemplate";
import { FormSection, SectionProps } from "../form_section";

export const RoleTypeFormSection = ({ id, title }: SectionProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const subtemplates = useActivityFormStore((state) => state.subtemplates);
  const template = useActivityFormStore((state) => state.template);
  const { isClone, isNew, isEdit } = useActivityFormStore((state) => state.activityStatus);
  const formSchema = useActivityFormStore((state) => state.formSchema);

  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    trigger
  } = useFormContext();

  const populateDefaultsEnabled = isNew && !isClone;

  const { isLoading } = useDynamicSubtemplateFields({
    subtemplate,
    enabled: populateDefaultsEnabled
  });

  const subtemplateOptions = useMemo<Option<Subtemplate>[]>(
    () =>
      subtemplates.map((subtemplate) => ({
        id: subtemplate.subtemplate_key,
        value: subtemplate.subtemplate_key,
        original: subtemplate,
        displayElement: subtemplate.subtemplate_title
      })),
    [subtemplates]
  );

  const clearAttributes = useCallback(
    (attributesToClear: string[]) => {
      const formValues = getValues();
      attributesToClear.forEach((fieldName) => {
        const keyInCustomFields = `${FIELD_CUSTOM_FIELDS_KEY}.${fieldName}`;
        const keyInMetadata = `${FIELD_METADATA_KEY}.${fieldName}`;
        if (get(formValues, keyInCustomFields)) {
          setValue(keyInCustomFields, undefined);
        }
        if (get(formValues, keyInMetadata)) {
          setValue(keyInMetadata, undefined);
        }
      });
    },
    [setValue, getValues]
  );

  const handleSubtemplateChange = (newSubtemplateKey: string) => {
    if (newSubtemplateKey === subtemplate?.subtemplate_key) {
      return;
    }
    if (subtemplate) {
      clearAttributes(map(formSchema?.[Sections.Attributes] || [], "name"));
    }
    setValue(FIELD_SUBTEMPLATE_KEY, newSubtemplateKey, { shouldDirty: true });
    trigger(FIELD_SUBTEMPLATE_KEY);
  };

  const required = isFieldRequired({ name: FIELD_SUBTEMPLATE_KEY });

  return (
    <FormSection id={id} title={title}>
      <Controller
        name={FIELD_SUBTEMPLATE_KEY}
        control={control}
        rules={{
          required: required ? t("errors.cantBeBlank") : false
        }}
        render={({ field }) => (
          <Select<Subtemplate>
            label={t("sections.subtemplate.template")}
            qaId="ActivityFormSubtemplateSelect"
            value={field.value}
            error={get(errors, field.name)?.message as string}
            options={subtemplateOptions}
            onChange={({ value }: Option<Subtemplate>) => handleSubtemplateChange(value)}
            disabled={isEdit || isClone || isLoading}
            required={required}
          />
        )}
      />
    </FormSection>
  );
};
